// Ändernde Operationen auf den Prozessdaten. Alle laufen über store.update (Undo-fähig).
import { update } from './store';
import { byId, newId, processTree, children } from './model.js';

const LANE_PALETTE = ['#009CDE', '#2A5BB0', '#15607F', '#2E8B57', '#C77700', '#93291E', '#6E7C85', '#241C63'];

/** Erste Bahn des Projekts; ein neues Projekt hat noch keine – dann wird eine Standardbahn angelegt. */
function ensureLane(d) {
  if (d.lanes[0]) return d.lanes[0].id;
  const id = newId('l', d);
  d.lanes.push({ id, name: d.meta?.kunde || 'Ablauf', farbe: LANE_PALETTE[0], beschreibung: '' });
  return id;
}

const blankNode = (pid, lane, typ, titel, d) => ({
  id: newId(pid, d),
  processId: pid,
  typ,
  lane,
  titel,
  systemIds: [],
  status: null,
  ausbaustufe2: false,
  nurZielbild: false,
  zielbild: '',
  beschreibung: '',
  verantwortlich: '',
  belege: [],
  input: '',
  output: '',
  quellen: [],
  notizen: '',
  subprocessId: null,
  decisionId: null,
});

export function setNodeField(id, field, value) {
  update((d) => {
    const n = byId(d.nodes, id);
    if (!n) return;
    n[field] = value;
    if (field === 'typ' && value === 'entscheidung' && !n.decisionId) {
      const dec = newDecision(d, n.processId, n.id, n.titel);
      n.decisionId = dec.id;
      n.status = 'offen';
    }
  }, { coalesce: 'node:' + id + ':' + field });
}

export function setEdgeField(id, field, value) {
  update((d) => {
    const e = byId(d.edges, id);
    if (e) e[field] = value;
  }, { coalesce: 'edge:' + id + ':' + field });
}

export function setProcessField(id, field, value) {
  update((d) => {
    const p = byId(d.processes, id);
    if (p) p[field] = value;
  }, { coalesce: 'proc:' + id + ':' + field });
}

export function setDecisionField(id, field, value) {
  update((d) => {
    const x = byId(d.decisions, id);
    if (!x) return;
    // Titel des Entscheidungskastens folgt der Fragestellung, solange die Entscheidung offen ist
    if (field === 'frage' && x.status !== 'entschieden') {
      const n = x.nodeId ? byId(d.nodes, x.nodeId) : null;
      if (n && n.typ === 'entscheidung') n.titel = value.length > 70 ? value.slice(0, 68) + '…' : value;
    }
    x[field] = value;
  }, { coalesce: 'dec:' + id + ':' + field });
}

export function setVariantField(decId, idx, field, value) {
  update((d) => {
    const x = byId(d.decisions, decId);
    if (x && x.varianten[idx]) x.varianten[idx][field] = value;
  }, { coalesce: `var:${decId}:${idx}:${field}` });
}

export function addVariant(decId) {
  update((d) => {
    const x = byId(d.decisions, decId);
    const key = freierVariantenKey(x.varianten);
    x.varianten.push({ key, titel: 'Variante ' + key, beschreibung: '', pro: [], contra: [] });
  });
}
export function removeVariant(decId, idx) {
  update((d) => {
    const x = byId(d.decisions, decId);
    x.varianten.splice(idx, 1);
    // Buchstaben neu durchzählen; die gewählte Variante behält dabei ihren Bezug
    const alt = x.gewaehlt ? x.varianten.find((v) => v.key === x.gewaehlt) : null;
    x.varianten.forEach((v, i) => (v.key = String.fromCharCode(65 + i)));
    if (x.gewaehlt) x.gewaehlt = alt ? alt.key : null;
  });
}

/** Nächster freier Buchstabe für eine neue Variante (A, B, … Z, danach A2, B2, …) – nie doppelt,
 *  auch wenn Buchstaben durch Löschen, Import oder Zusammenführen Lücken haben. */
export function freierVariantenKey(varianten) {
  const belegt = new Set((varianten || []).map((v) => v.key));
  for (let runde = 1; ; runde++) {
    for (let i = 0; i < 26; i++) {
      const key = String.fromCharCode(65 + i) + (runde > 1 ? runde : '');
      if (!belegt.has(key)) return key;
    }
  }
}

function newDecision(d, pid, nodeId, frage) {
  const dec = {
    id: newId('E', d),
    processId: pid,
    nodeId,
    frage: frage || 'Neue Entscheidung',
    hintergrund: '',
    varianten: [
      { key: 'A', titel: 'Variante A', beschreibung: '', pro: [], contra: [] },
      { key: 'B', titel: 'Variante B', beschreibung: '', pro: [], contra: [] },
    ],
    empfehlung: '',
    verantwortlich: '',
    termin: '',
    status: 'offen',
    gewaehlt: null,
    quellen: [],
  };
  d.decisions.push(dec);
  return dec;
}

/** Neuen Schritt anlegen; optional als Nachfolger eines bestehenden Schritts. */
export function addNode(pid, { lane, typ = 'aktivitaet', after = null, titel, insert = false } = {}) {
  return update((d) => {
    const p = byId(d.processes, pid);
    const prev = after ? byId(d.nodes, after) : null;
    const l = lane || prev?.lane || p.laneIds[0] || ensureLane(d);
    const n = blankNode(pid, l, typ, titel || (typ === 'entscheidung' ? 'Neue Entscheidung' : 'Neuer Schritt'), d);
    d.nodes.push(n);
    if (typ === 'entscheidung') {
      n.decisionId = newDecision(d, pid, n.id, n.titel).id;
      n.status = 'offen';
    }
    if (prev && insert) {
      // In den Ablauf einfügen: bisherige Nachfolger hängen künftig am neuen Schritt
      for (const e of d.edges) if (e.processId === pid && e.from === prev.id) e.from = n.id;
    }
    if (prev) d.edges.push({ id: newId('e', d), processId: pid, from: prev.id, to: n.id, typ: 'manuell', label: '' });
    if (!p.laneIds.includes(l)) p.laneIds.push(l);
    return n.id;
  });
}

/**
 * Neuen Schritt aus der Symbolleiste ins Diagramm fallen lassen (ein Undo-Schritt).
 * - into: Verbindung, in die der Schritt eingefügt wird (von → neu → nach)
 * - after: Schritt, an den der neue als Nachfolger angehängt wird
 * - sonst: freie Stelle target { lane, col, row } wie beim Verschieben
 * @param {string} pid
 * @param {{ typ?: string, target?: any, layout?: any, after?: string | null, into?: string | null }} [opts]
 */
export function placeNode(pid, { typ = 'aktivitaet', target, layout, after = null, into = null } = {}) {
  return update((d) => {
    const p = byId(d.processes, pid);
    const kante = into ? byId(d.edges, into) : null;
    const prev = !kante && after ? byId(d.nodes, after) : null;
    const l = target?.lane || prev?.lane || p.laneIds[0] || ensureLane(d);
    const titel = typ === 'entscheidung' ? 'Neue Entscheidung' : typ === 'start' ? 'Start' : typ === 'ende' ? 'Ende' : 'Neuer Schritt';
    const n = blankNode(pid, l, typ, titel, d);
    d.nodes.push(n);
    if (typ === 'entscheidung') {
      n.decisionId = newDecision(d, pid, n.id, n.titel).id;
      n.status = 'offen';
    }
    if (kante) {
      d.edges.push({ id: newId('e', d), processId: pid, from: n.id, to: kante.to, typ: kante.typ, label: '' });
      kante.to = n.id;
    } else if (prev) {
      d.edges.push({ id: newId('e', d), processId: pid, from: prev.id, to: n.id, typ: 'manuell', label: '' });
    }
    if (!kante && target && layout) {
      // Stelle im Diagramm: Mindestspalte und Platz im Zellstapel wie bei moveNode
      // (ein Nachfolger landet trotzdem immer rechts von seinem Vorgänger)
      n.colHint = target.col > 0 ? target.col : null;
      const cell = d.nodes
        .filter((x) => x.processId === pid && x.id !== n.id && x.lane === l && layout.nodes[x.id]?.rank === target.col)
        .sort((a, b) => layout.nodes[a.id].row - layout.nodes[b.id].row);
      cell.splice(Math.min(target.row, cell.length), 0, n);
      cell.forEach((x, i) => (x.order = i));
    }
    if (!p.laneIds.includes(l)) p.laneIds.push(l);
    return n.id;
  });
}

export function deleteNode(id) {
  update((d) => {
    const n = byId(d.nodes, id);
    if (!n) return;
    d.nodes = d.nodes.filter((x) => x.id !== id);
    d.edges = d.edges.filter((e) => e.from !== id && e.to !== id);
    d.decisions = d.decisions.filter((x) => x.nodeId !== id);
  });
}

export function addEdge(pid, from, to) {
  if (from === to) return null;
  return update((d) => {
    const exists = d.edges.find((e) => e.from === from && e.to === to);
    if (exists) return exists.id;
    const e = { id: newId('e', d), processId: pid, from, to, typ: 'manuell', label: '' };
    d.edges.push(e);
    return e.id;
  });
}

export function deleteEdge(id) {
  update((d) => {
    d.edges = d.edges.filter((e) => e.id !== id);
  });
}

export function reverseEdge(id) {
  update((d) => {
    const e = byId(d.edges, id);
    [e.from, e.to] = [e.to, e.from];
  });
}

/**
 * Schritt per Drag & Drop verschieben. Gespeichert werden nur Hinweise:
 * Bahn, Mindestspalte (colHint) und Position im Zellstapel (order).
 */
export function moveNode(id, target, layout) {
  update((d) => {
    const n = byId(d.nodes, id);
    if (!n) return;
    n.lane = target.lane;
    n.colHint = target.col > 0 ? target.col : null;
    const cell = d.nodes
      .filter((x) => x.processId === n.processId && x.id !== id && x.lane === target.lane && layout.nodes[x.id]?.rank === target.col)
      .sort((a, b) => layout.nodes[a.id].row - layout.nodes[b.id].row);
    cell.splice(Math.min(target.row, cell.length), 0, n);
    cell.forEach((x, i) => (x.order = i));
    const p = byId(d.processes, n.processId);
    if (p && !p.laneIds.includes(target.lane)) p.laneIds.push(target.lane);
  });
}

export function resetNodePosition(id) {
  update((d) => {
    const n = byId(d.nodes, id);
    n.colHint = null;
    n.order = null;
  });
}

export function addProcess(parentId, titel = 'Neuer Prozess') {
  return update((d) => {
    const parent = parentId ? byId(d.processes, parentId) : null;
    const id = newId('p', d);
    const lanes = parent ? [...parent.laneIds] : d.lanes.slice(0, 3).map((l) => l.id);
    const order = Math.max(-1, ...children(d, parentId).map((c) => c.order ?? 0)) + 1;
    d.processes.push({ id, parentId: parentId || null, titel, kurz: '', beschreibung: '', laneIds: lanes, order });
    const first = lanes[0] || ensureLane(d);
    if (!lanes.includes(first)) lanes.push(first);
    const s = blankNode(id, first, 'start', 'Start', d);
    d.nodes.push(s);
    const e = blankNode(id, first, 'ende', 'Ende', d);
    d.nodes.push(e);
    d.edges.push({ id: newId('e', d), processId: id, from: s.id, to: e.id, typ: 'manuell', label: '' });
    return id;
  });
}

export function createSubprocessForNode(nodeId) {
  return update((d) => {
    const n = byId(d.nodes, nodeId);
    const parent = byId(d.processes, n.processId);
    const id = newId('p', d);
    const order = Math.max(-1, ...children(d, parent.id).map((c) => c.order ?? 0)) + 1;
    d.processes.push({ id, parentId: parent.id, titel: n.titel, kurz: '', beschreibung: n.beschreibung || '', laneIds: [...parent.laneIds], order });
    const s = blankNode(id, n.lane, 'start', 'Start', d);
    const e = blankNode(id, n.lane, 'ende', 'Ende', d);
    d.nodes.push(s, e);
    d.edges.push({ id: newId('e', d), processId: id, from: s.id, to: e.id, typ: 'manuell', label: '' });
    n.typ = 'subprozess';
    n.subprocessId = id;
    return id;
  });
}

export function deleteProcess(pid) {
  update((d) => {
    const ids = new Set(processTree(d, pid).map((p) => p.id));
    d.processes = d.processes.filter((p) => !ids.has(p.id));
    const nodeIds = new Set(d.nodes.filter((n) => ids.has(n.processId)).map((n) => n.id));
    d.nodes = d.nodes.filter((n) => !nodeIds.has(n.id));
    d.edges = d.edges.filter((e) => !ids.has(e.processId));
    d.decisions = d.decisions.filter((x) => !ids.has(x.processId));
    for (const n of d.nodes) if (n.subprocessId && ids.has(n.subprocessId)) n.subprocessId = null;
  });
}

export function moveProcess(pid, dir) {
  update((d) => {
    const p = byId(d.processes, pid);
    const sib = children(d, p.parentId);
    const i = sib.findIndex((x) => x.id === pid);
    const j = i + dir;
    if (j < 0 || j >= sib.length) return;
    sib.forEach((x, k) => (x.order = k));
    [sib[i].order, sib[j].order] = [sib[j].order, sib[i].order];
  });
}

/** Neue Bahn anlegen und dem Prozess hinzufügen. */
export function addLaneToProcess(pid, name) {
  return update((d) => {
    const id = newId('l', d);
    d.lanes.push({ id, name, farbe: LANE_PALETTE[d.lanes.length % LANE_PALETTE.length], beschreibung: '' });
    byId(d.processes, pid).laneIds.push(id);
    return id;
  });
}

export function setProcessLanes(pid, laneIds) {
  update((d) => {
    byId(d.processes, pid).laneIds = laneIds;
  });
}

/** Entscheidung treffen: gewählte Variante wird zum Prozessschritt.
 *  `wahl` ist die Position der Variante (eindeutig, auch bei doppelten Buchstaben in Altdaten) oder ihr Buchstabe. */
export function decide(decId, wahl, begruendung = '') {
  update((d) => {
    const x = byId(d.decisions, decId);
    const v = typeof wahl === 'number' ? x.varianten[wahl] : x.varianten.find((v) => v.key === wahl);
    if (!v) return;
    // Doppelter Buchstabe (Altdaten): die gewählte Variante bekommt einen freien, damit `gewaehlt` eindeutig ist
    if (x.varianten.some((w) => w !== v && w.key === v.key)) v.key = freierVariantenKey(x.varianten);
    x.status = 'entschieden';
    x.gewaehlt = v.key;
    x.begruendung = begruendung;
    const n = x.nodeId ? byId(d.nodes, x.nodeId) : null;
    if (n) {
      const datum = new Date().toLocaleDateString('de-DE');
      n.typ = 'aktivitaet';
      n.titel = v.titel;
      n.status = 'in_arbeit';
      n.beschreibung = `**Entschieden am ${datum}: Variante ${v.key} – ${v.titel}**\n\n${v.beschreibung || ''}${begruendung ? `\n\nBegründung: ${begruendung}` : ''}`.trim();
    }
  });
}

export function reopenDecision(decId) {
  update((d) => {
    const x = byId(d.decisions, decId);
    x.status = 'offen';
    x.gewaehlt = null;
    const n = x.nodeId ? byId(d.nodes, x.nodeId) : null;
    if (n) {
      n.typ = 'entscheidung';
      n.titel = x.frage.length > 70 ? x.frage.slice(0, 68) + '…' : x.frage;
      n.status = 'offen';
    }
  });
}

/** Ausschnitt (Prozess inkl. Unterprozesse) als eigenständige Daten. */
export function subsetForProcess(data, pid) {
  const procs = processTree(data, pid).map((p) => ({ ...p }));
  const ids = new Set(procs.map((p) => p.id));
  procs[0].parentId = null;
  const nodes = data.nodes.filter((n) => ids.has(n.processId)).map((n) => ({ ...n, subprocessId: n.subprocessId && ids.has(n.subprocessId) ? n.subprocessId : null }));
  const laneIds = new Set([...nodes.map((n) => n.lane), ...procs.flatMap((p) => p.laneIds)]);
  return {
    schemaVersion: data.schemaVersion,
    meta: { ...data.meta, titel: procs[0].titel },
    lanes: data.lanes.filter((l) => laneIds.has(l.id)),
    systems: data.systems,
    edgeTypes: data.edgeTypes,
    processes: procs,
    nodes,
    edges: data.edges.filter((e) => ids.has(e.processId)),
    decisions: data.decisions.filter((x) => ids.has(x.processId)),
  };
}

/**
 * Einen Prozess samt Unterprozessen auf einen früheren Stand zurückholen: Schritte, Verbindungen und Entscheidungen werden
 * durch die des Stands ersetzt (gleiche IDs – Kommentare, Aufgaben und Tests bleiben zugeordnet). Fehlende Bahnen, Systeme
 * und Verbindungsarten kommen aus dem Stand dazu. Wie jede Bearbeitung: erst mit „Speichern“ übernommen, rückgängig machbar.
 * Gibt false zurück, wenn es den Prozess im Stand nicht gibt.
 */
export function prozessZurueckholen(stand, pid) {
  const alt = processTree(stand, pid);
  if (alt.length === 0) return false;
  update((d) => {
    const altIds = new Set(alt.map((p) => p.id));
    const weg = new Set([...processTree(d, pid).map((p) => p.id), ...altIds]);
    const eltern = byId(d.processes, pid)?.parentId ?? alt[0].parentId ?? null;
    for (const k of ['lanes', 'systems', 'edgeTypes']) {
      for (const x of stand[k] || []) if (!byId(d[k], x.id)) d[k].push(structuredClone(x));
    }
    d.processes = d.processes.filter((p) => !weg.has(p.id));
    d.nodes = d.nodes.filter((n) => !weg.has(n.processId));
    d.edges = d.edges.filter((e) => !weg.has(e.processId));
    d.decisions = d.decisions.filter((x) => !weg.has(x.processId));
    for (const p of alt) d.processes.push({ ...structuredClone(p), parentId: p.id === pid ? (eltern && byId(d.processes, eltern) ? eltern : null) : p.parentId });
    const gibt = new Set(d.processes.map((p) => p.id));
    const dec = new Set();
    for (const x of stand.decisions.filter((x) => altIds.has(x.processId))) { d.decisions.push(structuredClone(x)); dec.add(x.id); }
    for (const n of stand.nodes.filter((n) => altIds.has(n.processId))) {
      d.nodes.push({ ...structuredClone(n), subprocessId: n.subprocessId && gibt.has(n.subprocessId) ? n.subprocessId : null, decisionId: n.decisionId && dec.has(n.decisionId) ? n.decisionId : null });
    }
    for (const e of stand.edges.filter((e) => altIds.has(e.processId))) d.edges.push(structuredClone(e));
    // Verweise aus anderen Prozessen auf Unterprozesse, die es im Stand noch nicht gab
    for (const n of d.nodes) if (n.subprocessId && !gibt.has(n.subprocessId)) n.subprocessId = null;
  });
  return true;
}

/** Importierte Prozesse in die aktuellen Daten einfügen; kollidierende IDs werden umbenannt. */
export function mergeImport(imported, parentId = null) {
  return update((d) => {
    const taken = new Set([...d.processes, ...d.nodes, ...d.edges, ...d.decisions].map((x) => x.id));
    const map = new Map();
    const remap = (id) => {
      if (!id) return id;
      if (map.has(id)) return map.get(id);
      let nid = id;
      while (taken.has(nid)) nid = id + '_' + Math.random().toString(36).slice(2, 5);
      taken.add(nid);
      map.set(id, nid);
      return nid;
    };
    for (const k of ['lanes', 'systems', 'edgeTypes']) {
      for (const x of imported[k]) if (!byId(d[k], x.id)) d[k].push(x);
    }
    const roots = [];
    for (const p of imported.processes) {
      const np = { ...p, id: remap(p.id), parentId: p.parentId ? remap(p.parentId) : parentId };
      if (!p.parentId) roots.push(np.id);
      d.processes.push(np);
    }
    for (const n of imported.nodes) d.nodes.push({ ...n, id: remap(n.id), processId: remap(n.processId), subprocessId: n.subprocessId ? remap(n.subprocessId) : null, decisionId: n.decisionId ? remap(n.decisionId) : null });
    for (const e of imported.edges) d.edges.push({ ...e, id: remap(e.id), processId: remap(e.processId), from: remap(e.from), to: remap(e.to) });
    for (const x of imported.decisions) d.decisions.push({ ...x, id: remap(x.id), processId: remap(x.processId), nodeId: x.nodeId ? remap(x.nodeId) : null });
    return roots;
  });
}

/** Verantwortlichen überall umbenennen (Schritte und Entscheidungen). */
export function renameVerantwortlich(from, to) {
  update((d) => {
    for (const x of [...d.nodes, ...d.decisions]) if ((x.verantwortlich || '').trim() === from) x.verantwortlich = to.trim();
  });
}

export function setMeta(field, value) {
  update((d) => {
    d.meta[field] = value;
  }, { coalesce: 'meta:' + field });
}

export function setCollectionItem(coll, id, field, value) {
  update((d) => {
    const x = byId(d[coll], id);
    if (x) x[field] = value;
  }, { coalesce: `${coll}:${id}:${field}` });
}

export function addCollectionItem(coll, item) {
  return update((d) => {
    const id = newId(coll.slice(0, 1), d);
    d[coll].push({ id, ...item });
    return id;
  });
}

export function removeCollectionItem(coll, id) {
  update((d) => {
    d[coll] = d[coll].filter((x) => x.id !== id);
    if (coll === 'systems') for (const n of d.nodes) n.systemIds = n.systemIds.filter((s) => s !== id);
    if (coll === 'lanes') for (const p of d.processes) p.laneIds = p.laneIds.filter((l) => l !== id);
  });
}

/** Status auf alle Schritte eines Prozesses übertragen (Start/Ende und offene Entscheidungen ausgenommen). */
export function setStatusForSteps(pid, status, withChildren = false) {
  return update((d) => {
    const ids = new Set(withChildren ? processTree(d, pid).map((p) => p.id) : [pid]);
    let n = 0;
    for (const x of d.nodes) {
      if (!ids.has(x.processId) || x.typ === 'start' || x.typ === 'ende' || x.typ === 'entscheidung') continue;
      x.status = status;
      n++;
    }
    for (const p of d.processes) {
      if (ids.has(p.id)) p.status = status;
    }
    return n;
  });
}
